import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  TemplateRef
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { CustomValueAccessorComponent } from '../custom-value-accessor.component';
import { ISdFormComponentItem } from '../form-component-interface.model';
import { BrandsEnum } from '../../../_enums/brands.enum';
import { TranslateModule } from '@ngx-translate/core';
import { SdFormCheckboxComponent } from '../form-checkbox/form-checkbox.component';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ErrorTranslateService } from '../error-translate-service';
import { BaseFormErrorTranslateService } from '../form-common-error/base-form-error-translate.service';

/**
 * Checkbox Component.
 * The component is used in the following way:
 * ```
 * <sd-form checkbox-group
 *   class="c__btn-group"
 *   id="form_checkbox_group_stock_echeance"
 *   formControlName="stock_echeance"
 *   [nameField]="'stock_echeance'"
 *   [labelTitle]="'stock.echeance' | translate"
 *   [items]="itemsStockEcheance">
 * </sd-form>
 * ```
 */
@Component({
  selector: 'sd-form[checkbox-group], sd-form[checkbox-group-inline]',
  templateUrl: './form-checkbox-group.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SdFormCheckboxGroupComponent),
      multi: true
    },
    { provide: BaseFormErrorTranslateService, useClass: ErrorTranslateService }
  ],
  standalone: true,
  imports: [
    NgClass,
    SdFormCheckboxComponent,
    NgTemplateOutlet,
    TranslateModule
  ]
})
export class SdFormCheckboxGroupComponent
  extends CustomValueAccessorComponent<ISdFormComponentItem[]>
  implements OnDestroy
{
  @Input()
  brand: BrandsEnum;

  /**
   * Label of the form element
   */
  @Input()
  labelTitle: string;

  /**
   * Allows to hide the label
   */
  @Input()
  labelHidden = false;

  /**
   * ID of the form element (html attribute)
   */
  @Input()
  idField = '';

  /**
   * Name of the form element (html attribute)
   */
  @Input()
  nameField: string;

  /**
   * Is the element mandatory?
   */
  @Input()
  required = false;

  @Input()
  customListClass: string;

  /**
   * List of possible options
   */
  @Input()
  items: ISdFormComponentItem[];

  @Input()
  groupLibelleTemplate: TemplateRef<any>;

  @Output()
  checkedChange = new EventEmitter<ISdFormComponentItem>();

  /**
   * Accessor to recalculate the current value of the component
   */
  get value(): ISdFormComponentItem[] {
    return this.items.filter((it) => it.checked);
  }

  updateCheckbox(event) {
    this.checkedChange.emit(event);
  }

  clickCheckbox(item) {
    item.checked = !item.checked;
  }
}
